import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormGroup } from '@mui/material';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setIsVisible(true);
    } else {
      try {
        const parsed = JSON.parse(consent);
        if (parsed.essential === undefined) {
           // Old format fallback
           setIsVisible(true);
        } else {
           setPreferences(parsed);
        }
      } catch(e) {
         // Not JSON, probably old format
         setIsVisible(true);
      }
    }
  }, []);

  const savePreferences = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    setPreferences(prefs);
    setIsVisible(false);
    setIsModalOpen(false);
  };

  const handleAcceptAll = () => {
    savePreferences({ essential: true, analytics: true, marketing: true });
  };

  const handleRejectAll = () => {
    savePreferences({ essential: true, analytics: false, marketing: false });
  };

  const handleSaveCustom = () => {
    savePreferences(preferences);
  };

  const handleToggle = (type) => (event) => {
    setPreferences({ ...preferences, [type]: event.target.checked });
  };

  if (!isVisible) return null;

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#1f2937',
          color: '#ffffff',
          padding: '16px 24px',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 9999,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.15)',
          gap: 2
        }}
      >
        <Typography variant="body2" sx={{ fontSize: '0.9rem', color: '#e5e7eb', flex: 1, mb: { xs: 2, md: 0 } }}>
          We use cookies to improve your experience, remember your cart, and keep you logged in. 
          Choose your cookie preferences below.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button 
            variant="outlined" 
            onClick={() => setIsModalOpen(true)}
            sx={{ 
              borderColor: '#e5e7eb', 
              color: '#e5e7eb', 
              fontWeight: 600,
              whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', borderColor: '#fff' }
            }}
          >
            Customize
          </Button>
          <Button 
            variant="outlined" 
            onClick={handleRejectAll}
            sx={{ 
              borderColor: '#ef4444', 
              color: '#ef4444', 
              fontWeight: 600,
              whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)', borderColor: '#dc2626' }
            }}
          >
            Reject All
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAcceptAll}
            sx={{ 
              backgroundColor: '#C8A054', 
              color: '#fff', 
              fontWeight: 600,
              whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: '#9A7428' }
            }}
          >
            Accept All
          </Button>
        </Box>
      </Box>

      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Customize Cookie Preferences</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            We use essential cookies to make our site work. With your consent, we may also use non-essential cookies to improve user experience and analyze website traffic.
          </Typography>
          <FormGroup sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">Essential Cookies</Typography>
                <Typography variant="body2" color="text.secondary">Required for the website to function properly. Cannot be disabled.</Typography>
              </Box>
              <Switch checked={true} disabled color="primary" />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">Analytics Cookies</Typography>
                <Typography variant="body2" color="text.secondary">Help us understand how visitors interact with the website.</Typography>
              </Box>
              <Switch checked={preferences.analytics} onChange={handleToggle('analytics')} color="primary" />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">Marketing Cookies</Typography>
                <Typography variant="body2" color="text.secondary">Used to deliver relevant advertisements to users.</Typography>
              </Box>
              <Switch checked={preferences.marketing} onChange={handleToggle('marketing')} color="primary" />
            </Box>
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectAll} color="inherit">Reject All</Button>
          <Button onClick={handleSaveCustom} variant="contained" sx={{ backgroundColor: '#C8A054', '&:hover': { backgroundColor: '#9A7428' } }}>
            Save Preferences
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieBanner;
